import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';

const { t } = i18n;

interface PartnerOpt {
  id: string;
  code: string;
  name: string;
}

export function OrderNewPage() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<PartnerOpt[]>([]);
  const [partnerId, setPartnerId] = useState('');
  const [notes, setNotes] = useState('');
  const [requestedWorkDate, setRequestedWorkDate] = useState('');
  const [partnerProjectRef, setPartnerProjectRef] = useState('');
  const [partnerProjectName, setPartnerProjectName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<PartnerOpt[]>('/partners')
      .then((rows) => {
        setPartners(rows);
      })
      .catch(() => setPartners([]));
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const created = await api<{ id: string }>('/orders', {
        method: 'POST',
        body: {
          partnerId,
          requestedWorkDate: requestedWorkDate || undefined,
          notes: notes || undefined,
          partnerProjectRef: partnerProjectRef.trim() || undefined,
          partnerProjectName: partnerProjectName.trim() || undefined,
        },
      });
      navigate(`/orders/${created.id}`);
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="max-w-md space-y-4">
      <h2 className="text-xl font-semibold">{t('order.create.title')}</h2>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <label className="block space-y-1">
          <span className="font-medium">{t('order.col.partner')}</span>
          <select
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="">{t('common.select')}</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('order.requested_work_date.choose')}</span>
          <input
            type="date"
            value={requestedWorkDate}
            onChange={(e) => setRequestedWorkDate(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="font-medium">{t('order.col.project_ref')}</span>
            <input
              value={partnerProjectRef}
              onChange={(e) => setPartnerProjectRef(e.target.value)}
              maxLength={120}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
              placeholder={t('order.col.project_ref_hint')}
            />
          </label>
          <label className="block space-y-1">
            <span className="font-medium">{t('order.col.project_name')}</span>
            <input
              value={partnerProjectName}
              onChange={(e) => setPartnerProjectName(e.target.value)}
              maxLength={200}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
        </div>
        <label className="block space-y-1">
          <span className="font-medium">{t('order.col.notes')}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        {error ? <div className="text-red-600">{error}</div> : null}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={busy || !partnerId}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('order.create.submit')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </section>
  );
}
